import React, { useState } from 'react';
import {
  Box, Container, Grid, Typography, Button, Card, CardContent,
  Rating, useTheme, Stack, Paper, IconButton,
} from '@mui/material';
import {
  MedicalServices, LocalHospital, People,
  ArrowForward, Warning, Phone, CalendarMonth, InfoOutlined,
  ChevronRight, ArrowBackIos, ArrowForwardIos,
  DirectionsCar, Shield, ManageAccounts, Work, Groups,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { departments, newsItems, testimonials, emergencyNumbers } from '../data/hospitalData';

const HomePage: React.FC = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const theme = useTheme();

  const whyReasons = [
    { title: t.home.why1Title, desc: t.home.why1Desc, icon: '👨‍⚕️' },
    { title: t.home.why2Title, desc: t.home.why2Desc, icon: '🏥' },
    { title: t.home.why3Title, desc: t.home.why3Desc, icon: '🚨' },
    { title: t.home.why4Title, desc: t.home.why4Desc, icon: '❤️' },
    { title: t.home.why5Title, desc: t.home.why5Desc, icon: '💰' },
    { title: t.home.why6Title, desc: t.home.why6Desc, icon: '🌍' },
  ];

  const quickCards = [
    { label: t.home.quick1, icon: <People />, path: '/doctors', color: '#0B4F8A' },
    { label: t.home.quick2, icon: <CalendarMonth />, path: '/appointment', color: '#00897B' },
    { label: t.home.quick3, icon: <Warning />, path: '/emergency', color: '#D32F2F' },
    { label: t.home.quick4, icon: <InfoOutlined />, path: '/patients', color: '#6A1B9A' },
  ];

  const quickLinks = [
    { icon: <InfoOutlined />, labelEn: 'Patient Information', labelFr: 'Informations Patients', path: '/patients' },
    { icon: <CalendarMonth />, labelEn: 'Book Appointment', labelFr: 'Prendre Rendez-vous', path: '/appointment' },
    { icon: <Shield />, labelEn: 'Quality Standards', labelFr: 'Standards de Qualité', path: '/about' },
    { icon: <DirectionsCar />, labelEn: 'Getting Here', labelFr: 'Accès & Transport', path: '/contact' },
    { icon: <ManageAccounts />, labelEn: 'Hospital Management', labelFr: 'Direction de l\'Hôpital', path: '/about' },
    { icon: <Work />, labelEn: 'Careers', labelFr: 'Carrières', path: '/contact' },
  ];

  const [testimonialIdx, setTestimonialIdx] = useState(0);
  const prevTestimonial = () => setTestimonialIdx((i) => (i === 0 ? testimonials.length - 1 : i - 1));
  const nextTestimonial = () => setTestimonialIdx((i) => (i === testimonials.length - 1 ? 0 : i + 1));

  return (
    <Box>
      {/* HERO - Full photo */}
      <Box
        sx={{
          backgroundImage: 'url("/images/hospital-aerial.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: { xs: 240, sm: 340, md: 460 },
          width: '100%',
        }}
      />

      {/* Department cards — overlap the bottom of the photo */}
      <Box
        sx={{
          mt: { xs: '-60px', md: '-80px' },
          position: 'relative',
          zIndex: 1,
          pb: 4,
        }}
      >
        <Container maxWidth="md">
          <Grid container spacing={2} justifyContent="center">
            {departments.slice(0, 4).map((dept) => (
              <Grid item xs={6} sm={3} key={dept.id}>
                <Card
                  onClick={() => navigate(`/departments/${dept.id}`)}
                  sx={{
                    cursor: 'pointer',
                    textAlign: 'center',
                    py: 3,
                    px: 2,
                    boxShadow: '0 4px 24px rgba(0,0,0,0.13)',
                    borderRadius: 3,
                    bgcolor: 'background.paper',
                    '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 10px 32px rgba(0,0,0,0.18)' },
                    transition: 'all 0.25s ease',
                  }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      bgcolor: '#0B4F8A',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 1.5,
                    }}
                  >
                    <Typography sx={{ fontSize: '1.6rem', lineHeight: 1 }}>{dept.icon}</Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    color="text.secondary"
                    sx={{ fontSize: '0.88rem' }}
                  >
                    {language === 'en' ? dept.nameEn : dept.nameFr}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* PROMO BANNER */}
      <Box sx={{ bgcolor: '#0B4F8A', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="xl">
          <Grid container spacing={4} alignItems="center">

            {/* Left — CTA + badges */}
            <Grid item xs={12} md={6}>
              <Typography
                variant="h4"
                sx={{ color: 'white', fontWeight: 800, mb: 3, lineHeight: 1.3, fontSize: { xs: '1.6rem', md: '2rem' } }}
              >
                {language === 'en'
                  ? 'Book your appointment NOW at the Regional Hospital of Bafoussam!'
                  : 'Prenez votre rendez-vous MAINTENANT à l\'Hôpital Régional de Bafoussam !'}
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/appointment')}
                sx={{
                  bgcolor: 'white',
                  color: '#0B4F8A',
                  fontWeight: 700,
                  px: 4,
                  mb: 5,
                  '&:hover': { bgcolor: 'grey.100' },
                }}
              >
                {language === 'en' ? 'Book Now' : 'Réserver'}
              </Button>

              {/* Award badges */}
              <Stack direction="row" spacing={2} flexWrap="wrap" gap={2}>
                {[
                  { top: language === 'en' ? 'ACCREDITED' : 'ACCRÉDITÉ', mid: language === 'en' ? 'HOSPITAL' : 'HÔPITAL', year: '2024', sub: language === 'en' ? 'Ministry of Health' : 'Ministère de la Santé' },
                  { top: language === 'en' ? 'TOP' : 'TOP', mid: language === 'en' ? 'HOSPITAL' : 'HÔPITAL', year: '2024', sub: language === 'en' ? 'West Cameroon' : 'Région de l\'Ouest' },
                  { top: 'ISO', mid: '9001', year: '2015', sub: language === 'en' ? 'Quality Certified' : 'Certifié Qualité' },
                ].map((badge, i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 110,
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '2px solid rgba(255,255,255,0.3)',
                      textAlign: 'center',
                    }}
                  >
                    <Box sx={{ bgcolor: '#D32F2F', py: 0.5 }}>
                      <Typography sx={{ color: 'white', fontWeight: 900, fontSize: '0.7rem', letterSpacing: '0.05em' }}>
                        {badge.top}
                      </Typography>
                    </Box>
                    <Box sx={{ bgcolor: '#B71C1C', py: 0.4 }}>
                      <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.62rem' }}>
                        {badge.mid}
                      </Typography>
                    </Box>
                    <Box sx={{ bgcolor: '#8B9467', py: 1 }}>
                      <Typography sx={{ color: 'white', fontWeight: 900, fontSize: '1.1rem' }}>
                        {badge.year}
                      </Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.58rem', px: 0.5 }}>
                        {badge.sub}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Grid>

            {/* Right — News card */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{ borderRadius: 3, p: { xs: 3, md: 4 }, bgcolor: 'white' }}
              >
                <Typography
                  variant="overline"
                  sx={{ color: '#0B4F8A', fontWeight: 700, letterSpacing: '0.1em', fontSize: '0.75rem' }}
                >
                  {language === 'en' ? 'Just announced!' : 'Juste annoncé !'}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 800, color: 'text.primary', mt: 1, mb: 2, lineHeight: 1.35 }}
                >
                  {language === 'en' ? newsItems[0].titleEn : newsItems[0].titleFr}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8, mb: 3 }}>
                  {language === 'en' ? newsItems[0].excerptEn : newsItems[0].excerptFr}
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate('/news')}
                  sx={{ bgcolor: '#0B4F8A', '&:hover': { bgcolor: '#083d6e' }, textTransform: 'none', fontWeight: 600 }}
                >
                  {language === 'en' ? 'Learn more' : 'En savoir plus'}
                </Button>
              </Paper>
            </Grid>

          </Grid>
        </Container>
      </Box>

      {/* PATIENT RIGHTS HIGHLIGHT */}
      <Box sx={{ bgcolor: 'white', py: { xs: 6, md: 10 }, borderTop: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">

            {/* Left — text */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight={600} color="text.secondary" mb={0.5}>
                {language === 'en' ? 'Your Rights at Our Hospital' : 'Vos Droits à Notre Hôpital'}
              </Typography>
              <Box sx={{ width: 80, height: 3, bgcolor: 'primary.main', mb: 2, borderRadius: 2 }} />
              <Typography variant="body1" color="text.secondary" lineHeight={1.9} mb={3} maxWidth={440}>
                {language === 'en'
                  ? 'You have the right to receive care at the hospital of your choice. As a patient, you are entitled to choose the most suitable healthcare facility for your needs.'
                  : 'Vous avez le droit de recevoir des soins à l\'hôpital de votre choix. En tant que patient, vous avez le droit de choisir l\'établissement de santé le mieux adapté à vos besoins.'}
              </Typography>
              <Typography variant="h4" fontWeight={800} color="text.primary" mb={3} sx={{ lineHeight: 1.25 }}>
                {language === 'en' ? 'Patient Rights & Information' : 'Droits & Informations Patients'}
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/patients')}
                endIcon={<ArrowForward />}
                sx={{ textTransform: 'uppercase', fontWeight: 700, borderRadius: 6, px: 4 }}
              >
                {language === 'en' ? 'Learn More' : 'En savoir plus'}
              </Button>
            </Grid>

            {/* Right — illustration */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', gap: 3 }}>
                {[52, 68, 52].map((size, i) => (
                  <Box key={i} sx={{ textAlign: 'center' }}>
                    <Box sx={{
                      width: size + 20, height: size + 20, borderRadius: 3,
                      bgcolor: i === 1 ? '#0B4F8A' : '#e8f0fb',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: i === 1 ? 4 : 1,
                    }}>
                      <LocalHospital sx={{ fontSize: size, color: i === 1 ? 'white' : '#0B4F8A' }} />
                    </Box>
                    <Box sx={{ width: 2, height: 24, mx: 'auto', borderLeft: '2px dashed #aaa' }} />
                  </Box>
                ))}
              </Box>
              <Box sx={{ textAlign: 'center', mt: 1 }}>
                <Box sx={{ width: 100, height: 100, borderRadius: '50%', bgcolor: '#e8f0fb', mx: 'auto', mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ fontSize: '3.5rem' }}>🧑‍⚕️</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  {language === 'en' ? 'Choose your hospital' : 'Choisissez votre hôpital'}
                </Typography>
              </Box>
            </Grid>

          </Grid>
        </Container>
      </Box>

      {/* NEWS */}
      <Box sx={{ bgcolor: '#f5f7fa', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="xl">
          {/* Centered title with blue underline */}
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography variant="h4" fontWeight={700} color="text.primary" mb={1}>
              {language === 'en' ? 'News' : 'Actualités'}
            </Typography>
            <Box sx={{ width: 60, height: 3, bgcolor: 'primary.main', mx: 'auto', borderRadius: 2 }} />
          </Box>

          <Grid container spacing={3}>
            {newsItems.slice(0, 3).map((item) => (
              <Grid item xs={12} sm={4} key={item.id}>
                <Card
                  onClick={() => navigate('/news')}
                  sx={{
                    cursor: 'pointer', height: '100%',
                    transition: 'all 0.2s',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 },
                  }}
                >
                  <Box sx={{ height: 200, bgcolor: item.image, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <MedicalServices sx={{ fontSize: 50, color: 'rgba(255,255,255,0.35)' }} />
                  </Box>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="subtitle1" fontWeight={700} mb={1} sx={{ lineHeight: 1.4 }}>
                      {language === 'en' ? item.titleEn : item.titleFr}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" lineHeight={1.8}>
                      {(language === 'en' ? item.excerptEn : item.excerptFr).slice(0, 140)}…
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button variant="outlined" size="large" endIcon={<ArrowForward />} onClick={() => navigate('/news')}>
              {t.common.viewAll}
            </Button>
          </Box>
        </Container>
      </Box>

      {/* TEAM PHOTO + ABOUT */}
      <Box sx={{ bgcolor: theme.palette.mode === 'dark' ? 'background.default' : 'white', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="xl">
          <Grid container spacing={5} alignItems="center">
            {/* Left — photo */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  width: '100%',
                  height: { xs: 260, md: 400 },
                  backgroundImage: 'url("/images/hospital-team.jpg")',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center top',
                  borderRadius: 2,
                  bgcolor: '#c8d8e8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Groups sx={{ fontSize: 80, color: 'rgba(11,79,138,0.25)' }} />
              </Box>
            </Grid>
            {/* Right — text */}
            <Grid item xs={12} md={6}>
              <Typography variant="h4" fontWeight={800} color="primary.main" mb={2}>
                {language === 'en' ? 'Hôpital Régional de Bafoussam' : 'Hôpital Régional de Bafoussam'}
              </Typography>
              <Typography variant="body1" color="text.secondary" lineHeight={1.9} mb={2}>
                {language === 'en'
                  ? 'Our hospital provides you with up-to-date and comprehensive information on medical care in the West Region of Cameroon. With expert specialists in Surgery, Internal Medicine, Pediatrics and Gynecology, we are committed to your health every day.'
                  : 'Notre hôpital vous offre des informations complètes et actualisées sur les soins médicaux dans la Région de l\'Ouest du Cameroun. Avec des spécialistes experts en Chirurgie, Médecine Interne, Pédiatrie et Gynécologie, nous nous engageons chaque jour pour votre santé.'}
              </Typography>
              <Typography variant="body1" color="text.secondary" lineHeight={1.9} mb={1}>
                {language === 'en'
                  ? 'Discover our services and medical team. You can also get an overview of our hospital through our photo gallery.'
                  : 'Découvrez nos services et notre équipe médicale. Vous pouvez également vous faire une idée de notre hôpital à travers notre galerie photos.'}
              </Typography>
              <Typography variant="body2" color="text.secondary" lineHeight={1.9} mb={3}>
                {language === 'en'
                  ? 'If you have further questions, please call us or send us an email.'
                  : 'Si vous avez d\'autres questions, appelez-nous ou envoyez-nous un e-mail.'}
              </Typography>
              <Typography variant="body2" fontWeight={600} color="text.primary" mb={3}>
                {language === 'en' ? 'The team of Hôpital Régional de Bafoussam' : 'L\'équipe de l\'Hôpital Régional de Bafoussam'}
              </Typography>
              <Stack direction="row" spacing={2}>
                <Button variant="contained" size="large" onClick={() => navigate('/contact')}
                  sx={{ textTransform: 'none', fontWeight: 700 }}>
                  {language === 'en' ? 'Contact Us' : 'Nous Contacter'}
                </Button>
                <Button variant="outlined" size="large" onClick={() => navigate('/about')}
                  sx={{ textTransform: 'none', fontWeight: 600 }}>
                  {language === 'en' ? 'Learn More' : 'En savoir plus'}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* TESTIMONIALS — card slider with arrows */}
      <Box sx={{ bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : '#f5f7fa', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Prev arrow */}
            <IconButton onClick={prevTestimonial}
              sx={{ flexShrink: 0, border: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'primary.main', color: 'white' } }}>
              <ArrowBackIos sx={{ fontSize: 16, pl: 0.5 }} />
            </IconButton>

            {/* 3 visible cards */}
            <Grid container spacing={2}>
              {[0, 1, 2].map((offset) => {
                const t2 = testimonials[(testimonialIdx + offset) % testimonials.length];
                return (
                  <Grid item xs={12} sm={4} key={offset}>
                    <Card sx={{
                      height: '100%', p: 0.5, position: 'relative', overflow: 'visible',
                      boxShadow: offset === 0 ? 4 : 1,
                    }}>
                      {/* Navy corner triangle */}
                      <Box sx={{
                        position: 'absolute', top: 0, right: 0,
                        width: 0, height: 0,
                        borderStyle: 'solid',
                        borderWidth: '0 36px 36px 0',
                        borderColor: `transparent #0B4F8A transparent transparent`,
                      }} />
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700, fontSize: '0.65rem', letterSpacing: '0.08em' }}>
                          {t2.dept}
                        </Typography>
                        <Typography variant="caption" color="text.disabled" display="block" mb={1.5}>
                          {new Date().toLocaleDateString(language === 'en' ? 'en-GB' : 'fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </Typography>
                        <Rating value={t2.rating} readOnly size="small" sx={{ mb: 1.5, '& .MuiRating-iconFilled': { color: '#0B4F8A' } }} />
                        <Typography variant="body2" color="text.secondary" lineHeight={1.8} fontStyle="italic">
                          "{language === 'en' ? t2.textEn : t2.textFr}"
                        </Typography>
                        <Typography variant="caption" fontWeight={700} color="text.primary" display="block" mt={2}>
                          — {t2.name}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {/* Next arrow */}
            <IconButton onClick={nextTestimonial}
              sx={{ flexShrink: 0, border: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'primary.main', color: 'white' } }}>
              <ArrowForwardIos sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Container>
      </Box>

      {/* QUICK LINKS GRID */}
      <Box sx={{ bgcolor: 'white', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={2}>
            {quickLinks.map((link, i) => (
              <Grid item xs={12} sm={4} key={i}>
                <Box
                  onClick={() => navigate(link.path)}
                  sx={{
                    bgcolor: '#0B4F8A',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    px: 3,
                    py: 2.5,
                    cursor: 'pointer',
                    borderRadius: 1,
                    transition: 'all 0.2s',
                    '&:hover': { bgcolor: '#083d6e', transform: 'translateY(-2px)' },
                  }}
                >
                  <Box sx={{ '& svg': { fontSize: 28, color: 'rgba(255,255,255,0.85)' } }}>
                    {link.icon}
                  </Box>
                  <Typography fontWeight={600} sx={{ fontSize: '0.95rem', color: 'white' }}>
                    {language === 'en' ? link.labelEn : link.labelFr}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA BANNER */}
      <Box
        sx={{
          py: 7,
          textAlign: 'center',
          bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="sm">
          <Typography variant="h4" fontWeight={700} mb={2}>
            {language === 'en' ? 'Ready to Book Your Appointment?' : 'Prêt à Prendre Rendez-vous ?'}
          </Typography>
          <Typography color="text.secondary" mb={4}>
            {language === 'en'
              ? 'Our team is ready to assist you. Book online or call us directly.'
              : 'Notre équipe est prête à vous assister. Réservez en ligne ou appelez-nous directement.'}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button variant="contained" size="large" onClick={() => navigate('/appointment')} endIcon={<ChevronRight />}>
              {t.common.bookAppointment}
            </Button>
            <Button variant="outlined" size="large" onClick={() => navigate('/contact')} startIcon={<Phone />}>
              {emergencyNumbers.main}
            </Button>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;
